import {
  CrestType,
  EquippedItem,
  UpgradeTrack,
} from 'app/frontend/types/custom'
import { upgradeTracks } from '../../../../types/custom/UpgradeTrack'
import { crestTypes } from '../../../../types/custom/CrestType'
import { useMemo } from 'react'
import ItemIcon from '../../../components/ItemIcon'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { crestEarnableCap } from 'app/frontend/base/helpers/crestCap'
import CrestCapTooltip from 'app/frontend/base/components/CrestCapTooltip'

interface Props {
  character?: wowaudit.Character
}

const colorByCrestType = {
  Myth: 'legendary',
  Hero: 'epic',
  Champion: 'rare',
  Veteran: 'uncommon',
}

const UpgradesSummary = ({ character }: Props) => {
  const {
    data: { currentPeriod, currentSeason },
  } = usePageInfo()

  const liStyles =
    'whitespace-nowrap bg-card-nested border my-1 py-1 px-2 rounded-md'

  const upgrades = useMemo(() => {
    if (!character?.details?.gear) return null

    return Object.values(character.details.gear).reduce((acc, item) => {
      if (!item.upgrade_level || item.upgrade_level.track === 'None') return acc

      const track = item.upgrade_level.track
      if (!acc[track]) acc[track] = []

      const stepsRemaining =
        (item.upgrade_level.total || 0) - (item.upgrade_level.step || 0)
      const crest = crestTypes.find((c) => c === track)
      const costs = (crest ? { [crest]: stepsRemaining * 20 } : {}) as {
        [key in CrestType]: number
      }

      acc[track].push({ costs, item })

      return acc
    }, {} as Record<UpgradeTrack, { costs: { [key in CrestType]: number }; item: EquippedItem }[]>)
  }, [character?.details])

  return (
    <ul>
      {upgradeTracks.map((track) => {
        if (track === 'None') return null

        const records = upgrades?.[track] || []
        const upgradableAmount = records.reduce(
          (acc, record) =>
            acc +
            (record.item.upgrade_level?.total || 0) -
            (record.item.upgrade_level?.step || 0),
          0,
        )

        if (
          ['Adventurer', 'Explorer'].includes(track) &&
          upgradableAmount === 0
        )
          return null

        const costsByCrest = {} as { [key in CrestType]: number }
        records.forEach((record) => {
          Object.entries(record.costs).forEach(([key, amount]) => {
            const crest = key as CrestType
            if (!costsByCrest[crest]) costsByCrest[crest] = 0
            costsByCrest[crest] += amount
          })
        })

        return (
          <li key={track} className={liStyles}>
            <div>
              <div className='flex justify-between'>
                <span>
                  <strong>{track}</strong> track
                </span>
                <span>
                  <strong>{records.length}</strong> item
                  {records.length === 1 ? '' : 's'}
                </span>
              </div>

              {records.length > 0 && (
                <div className='flex justify-between mt-1 gap-2 flex-wrap'>
                  <span className='italic'>
                    <strong>{upgradableAmount}</strong> upgrades left
                  </span>

                  {upgradableAmount > 0 && (
                    <span className='flex gap-2'>
                      {Object.keys(costsByCrest).map((crest) => {
                        const crestType = currentSeason.crests.find(
                          (c) => c.name === crest,
                        )
                        return (
                          <span key={crest} className='flex gap-1 items-center'>
                            <strong
                              className={`text-quality-${
                                colorByCrestType[crest as CrestType]
                              }`}
                            >
                              {costsByCrest[crest as CrestType]}×
                            </strong>
                            <Tooltip delayDuration={150}>
                              <TooltipTrigger>
                                <ItemIcon id={crestType?.icon_item_id} />
                              </TooltipTrigger>
                              <TooltipContent>{crestType?.name}</TooltipContent>
                            </Tooltip>
                          </span>
                        )
                      })}
                    </span>
                  )}
                </div>
              )}
            </div>
          </li>
        )
      })}

      {character?.refreshedAt ? (
        <>
          <li className='mt-2 mb-1 pb-1 block border-b font-bold'>
            {currentSeason.crests_uncapped
              ? 'Crests earned'
              : 'Crests earnable'}
          </li>

          <li className='flex gap-1'>
            {currentSeason.crests.slice(0, 3).map((crestType) => {
              const period = currentPeriod[character.realm.region]
              const currentCap = crestEarnableCap(crestType, period)
              const earned =
                character.details?.crestsEarned?.[crestType.name] || 0
              const earnedSources =
                character.details?.uncappedCrestsEarned?.[crestType.name] ?? {}

              const card = (
                <>
                  {currentSeason.crests_uncapped ? (
                    <div className='text-xs opacity-70 text-center mb-1'>
                      {crestType.name}
                    </div>
                  ) : null}

                  <div className='flex items-center justify-center gap-2 mb-1'>
                    <ItemIcon id={crestType.icon_item_id} />
                    <strong
                      className={`text-quality-${
                        colorByCrestType[crestType.name]
                      }`}
                    >
                      {currentSeason.crests_uncapped
                        ? earned
                        : Math.max(0, currentCap - earned)}
                    </strong>
                  </div>

                  {!currentSeason.crests_uncapped && (
                    <div className='text-xs opacity-70 text-center'>
                      {earned} / {currentCap}
                    </div>
                  )}
                </>
              )

              if (!currentSeason.crests_uncapped) {
                return (
                  <CrestCapTooltip
                    key={crestType.name}
                    crest={crestType}
                    period={period}
                    earned={earned}
                    earnedSources={earnedSources}
                    trackName={crestType.name}
                    className={`${liStyles} grow`}
                  >
                    {card}
                  </CrestCapTooltip>
                )
              }

              return (
                <div key={crestType.name} className={`${liStyles} grow`}>
                  {card}
                </div>
              )
            })}
          </li>
        </>
      ) : null}
    </ul>
  )
}

export default UpgradesSummary
